import React, { useEffect, useState } from "react";
import { Wordmark } from "./Brand";

const links = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Membership", href: "#membership" },
  { label: "Contact", href: "#contact" },
];

const freedomStyles = `
  @keyframes freedomPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,149,0,.55), 0 0 0 0 rgba(200,16,46,.35); }
    50%       { box-shadow: 0 0 10px 4px rgba(255,149,0,.3), 0 0 22px 8px rgba(200,16,46,.12); }
  }
  @keyframes sparkSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .btn-freedom {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 8px 16px;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #fff;
    border: none;
    cursor: pointer;
    background: linear-gradient(135deg, #FF9500 0%, #C8102E 50%, #1A2B5F 100%);
    animation: freedomPulse 2s infinite;
    overflow: hidden;
    white-space: nowrap;
    transition: opacity 0.2s, transform 0.2s;
    font-family: inherit;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
  }
  .btn-freedom:hover { opacity: 0.9; transform: translateY(-1px); }
  .btn-freedom:active { opacity: 0.85; transform: translateY(0); }
  .btn-freedom::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%);
    border-radius: 9999px;
    pointer-events: none;
  }
  .freedom-spark {
    display: inline-block;
    animation: sparkSpin 3s linear infinite;
    font-style: normal;
    flex-shrink: 0;
  }

  /* Slide-down animation for mobile menu */
  @keyframes menuSlideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mobile-menu-enter {
    animation: menuSlideDown 0.2s ease forwards;
  }

  /* Respect reduced-motion preference */
  @media (prefers-reduced-motion: reduce) {
    .btn-freedom { animation: none; }
    .freedom-spark { animation: none; }
    .mobile-menu-enter { animation: none; }
  }
`;

const scrollToEventBanner = (e, closeMobile) => {
  e.preventDefault();
  const el = document.getElementById("ebc-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  if (closeMobile) closeMobile();
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const closeMobile = () => setOpen(false);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
      data-testid="navbar"
    >
      <style>{freedomStyles}</style>

      <div className="section">
        <nav
          className={`flex items-center justify-between rounded-full px-3 sm:px-5 py-2.5 transition-all duration-300 ${
            scrolled
              ? "glass shadow-soft"
              : "bg-white/40 backdrop-blur-md border border-white/40"
          }`}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Brand */}
          <a
            href="#top"
            className="flex items-center gap-2 flex-shrink min-w-0 overflow-hidden"
            data-testid="nav-brand-link"
          >
            <Wordmark context="header" />
          </a>

          {/* Desktop links — visible from lg up to avoid crowding at md */}
          {/* ml-6 ensures clear gap from the wordmark on Nest Hub / 1024px */}
          <ul className="hidden lg:flex items-center gap-1 ml-6">
            {/* FREEDOM button */}
            <li>
              <a
                href="#event-banner"
                className="btn-freedom"
                data-testid="nav-link-freedom"
                onClick={(e) => scrollToEventBanner(e)}
              >
                <i className="freedom-spark" aria-hidden="true">✦</i>
                FREEDOM
              </a>
            </li>

            {links.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="px-4 py-2 text-sm font-medium text-slate-700 rounded-full hover:bg-slate-900/5 hover:text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                >
                  {l.label} <span className="sr-only">(desktop menu)</span>
                </a>
              </li>
            ))}
          </ul>

          {/* Right side */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {/* Phone CTA — visible on lg+ only so it doesn't crowd the hamburger */}
            <a
             

  href="tel:+918274964884"
  className="hidden lg:inline-flex btn-primary !py-2.5 !px-5"
  data-testid="nav-cta-talk-to-expert"
  aria-label="Call IBH to talk to a business expert"
>
  <i className="fa-solid fa-headset text-xs" aria-hidden="true"></i>
  Talk to an Expert
</a>

            {/* Hamburger — visible below lg; min-w ensures it's never clipped */}
            <button
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              className="lg:hidden h-9 w-9 min-w-[2.25rem] rounded-full bg-white/80 border border-slate-200 flex items-center justify-center hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
            >
              <i
                className={`fa-solid ${open ? "fa-xmark" : "fa-bars"} text-slate-700`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </nav>

        {/* Mobile / tablet menu */}
        {open && (
          <div
            id="mobile-menu"
            className="lg:hidden mt-2 glass rounded-3xl p-4 shadow-soft mobile-menu-enter"
            data-testid="nav-mobile-menu"
          >
            <ul className="flex flex-col gap-1">
              {/* FREEDOM first */}
              <li>
                <a
                  href="#event-banner"
                  className="btn-freedom w-full mt-1 mb-1"
                  onClick={(e) => scrollToEventBanner(e, closeMobile)}
                  data-testid="nav-mobile-link-freedom"
                >
                  <i className="freedom-spark" aria-hidden="true">✦</i>
                  FREEDOM
                </a>
              </li>

              {links.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    onClick={closeMobile}
                    className="block px-4 py-3 rounded-2xl text-sm font-medium text-slate-700 hover:bg-slate-900/5 hover:text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                  >
                    {l.label} <span className="sr-only">(mobile menu)</span>
                  </a>
                </li>
              ))}

              {/* Phone CTA in mobile menu — links to actual tel: */}
              <li>
                <a
               

  href="tel:+918274964884"
  onClick={closeMobile}
  className="btn-primary w-full mt-2"
  data-testid="nav-mobile-cta"
  aria-label="Call IBH to talk to a business expert (mobile menu)"
>
  <i className="fa-solid fa-headset text-xs" aria-hidden="true"></i>
  Talk to an Expert
</a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;